import { focusLocation } from "../events/actions";
import { useFancyClothesDesign } from "../hooks/useFancyClothesDesign";
import { WebsiteEvent } from "../resources/modelTypes";
import InfoPopup from "./InfoPopup";
import { css } from "@emotion/react";
import { styleUtils } from "@minted/minted-components";
import React, { memo, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

const MARKER_WIDTH = 40;
const MARKER_HEIGHT = 50;
const MARKER_LEFT = -0.5 * MARKER_WIDTH; // center horizontally
const MARKER_TOP = -1 * MARKER_HEIGHT; // show above actual location

type MarkerEvent = Pick<
  WebsiteEvent,
  | "address1"
  | "address2"
  | "city"
  | "date"
  | "state"
  | "time"
  | "title"
  | "venue"
  | "zipCode"
>;

type MapMarkerProps = {
  events: MarkerEvent[];
  index: number;
  // lat and lng are used by google-map-react for positioning
  lat: number;
  lng: number;
  onCloseClick: () => void;
  showInfo: boolean;
};

const MapMarker = ({
  events,
  index,
  onCloseClick,
  showInfo,
}: MapMarkerProps) => {
  const dispatch = useDispatch();
  const { designProvider } = useFancyClothesDesign();
  const { backgroundColor, fontColor, fontFamily, fontSizeFactor } =
    designProvider;

  const handleClick = useCallback(() => {
    dispatch(focusLocation(index));
  }, [dispatch, index]);

  const markerStyles = useMemo(
    () => css`
      cursor: pointer;
      height: ${MARKER_HEIGHT}px;
      left: ${MARKER_LEFT}px;
      position: absolute;
      text-align: center;
      top: ${MARKER_TOP}px;
      width: ${MARKER_WIDTH}px;
    `,
    [],
  );

  const svgStyles = useMemo(
    () => css`
      fill: ${fontColor};
      left: 0;
      position: absolute;
      top: 0;
    `,
    [fontColor],
  );

  const textStyles = useMemo(
    () => css`
      color: ${backgroundColor};
      display: block;
      font-family: ${fontFamily};
      font-size: ${styleUtils.rem(16 * fontSizeFactor)};
      position: relative;
      top: 30%;
      transform: translateY(-20%);
      width: ${styleUtils.rem(40)};
    `,
    [backgroundColor, fontFamily, fontSizeFactor],
  );

  return (
    <div>
      <div css={markerStyles} onClick={handleClick}>
        <svg css={svgStyles} height="50px" width="40px" x="0px" y="0px">
          <filter id="dropshadow">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1" />
            <feOffset dx="0" dy="0" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA slope="0.4" type="linear" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <path
            d="M20,2C11.7,2,5,8.4,5,16.4C5,30.1,20,48,20,48s15-17.9,15-31.6C35,8.4,28.3,2,20,2z"
            style={{
              filter: "url(#dropshadow)",
            }}
          />
          {events.length > 1 && (
            <circle
              cx="50%"
              cy="40%"
              fill="none"
              r="25%"
              stroke="#fff"
              strokeWidth="5%"
            />
          )}
        </svg>
        <span css={textStyles}>{events.length > 1 ? "+" : `${index + 1}`}</span>
      </div>
      {showInfo && <InfoPopup events={events} onCloseClick={onCloseClick} />}
    </div>
  );
};

const MemoizedMapMarker = Object.assign(memo(MapMarker), {
  MARKER_LEFT,
  MARKER_TOP,
});

export default MemoizedMapMarker;
